/**
 * Wylicza optymalne skoki cenowe na podstawie zakresu min/max
 * @param {number} min - minimalna cena
 * @param {number} max - maksymalna cena
 * @returns {object} { step: number, tickCount: number, snapPoints: number[] }
 */
export const calculatePriceSteps = (min, max) => {
  const range = max - min;
  
  // Jeśli zakres jest za mały, używamy minimalnego skoku
  if (range <= 10000) {
    return {
      step: 10000,
      tickCount: 2,
      snapPoints: [min, max]
    };
  }
  
  // Możliwe skoki (wielokrotności 10000)
  const possibleSteps = [
    10000,    // 10k
    20000,    // 20k
    25000,    // 25k
    50000,    // 50k
    100000,   // 100k
    200000,   // 200k
    250000,   // 250k
    500000,   // 500k
    1000000,  // 1M
    2000000,  // 2M
    5000000   // 5M
  ];
  
  // Znajdź optymalny skok
  let bestStep = 10000;
  let bestTickCount = 10;
  
  for (const step of possibleSteps) {
    const tickCount = Math.floor(range / step) + 1;
    
    // Jeśli liczba skoków jest <= 10, to dobry kandydat
    if (tickCount <= 10) {
      bestStep = step;
      bestTickCount = tickCount;
      break;
    }
  }
  
  // Generuj punkty przyciągania z dostosowaniem granic dla równych kroków
  const snapPoints = [];
  
  // Sprawdź czy zakres dzieli się równo na bestStep
  if (range % bestStep === 0) {
    // Zakres dzieli się równo - użyj oryginalnych wartości
    for (let value = min; value <= max; value += bestStep) {
      snapPoints.push(value);
    }
  } else {
    // Zakres nie dzieli się równo - dostosuj granice
    const numberOfSteps = Math.floor(range / bestStep);
    const adjustedRange = numberOfSteps * bestStep;
    const padding = (range - adjustedRange) / 2;
    
    const adjustedMin = min + padding;
    const adjustedMax = adjustedMin + adjustedRange;
    
    // Generuj równe kroki między dostosowanymi granicami
    snapPoints.push(min); // Zachowaj oryginalny min
    for (let i = 1; i < numberOfSteps; i++) {
      snapPoints.push(adjustedMin + i * bestStep);
    }
    snapPoints.push(max); // Zachowaj oryginalny max
  }
  
  // Oblicz uniwersalny step który pozwala dotrzeć do min i max
  // Znajdź największy dzielnik zakresu, ale nie mniejszy niż 100
  let universalStep = 1000;
  const rangeValue = max - min;
  
  // Znajdź największy dzielnik zakresu >= 100
  for (let divisor = Math.min(10000, Math.floor(rangeValue / 10)); divisor >= 100; divisor -= 100) {
    if (rangeValue % divisor === 0) {
      universalStep = divisor;
      break;
    }
  }
  
  // Fallback: jeśli nie znaleziono dobrego dzielnika, użyj 1 (uniwersalny)
  if (rangeValue % universalStep !== 0) {
    universalStep = 1;
  }

  return {
    step: universalStep,      // uniwersalny step dla input range i snap points
    tickCount: snapPoints.length,
    snapPoints: snapPoints
  };
};

/**
 * Przykłady użycia:
 * 
 * calculatePriceSteps(300000, 900000)
 * // { step: 100000, tickCount: 7, snapPoints: [300000, 300000, 400000, 500000, 600000, 700000, 800000, 900000] }
 * 
 * calculatePriceSteps(450000, 650000)
 * // { step: 25000, tickCount: 9, snapPoints: [450000, 450000, 475000, 500000, 525000, 550000, 575000, 600000, 625000, 650000] }
 * 
 * calculatePriceSteps(1000000, 5000000)
 * // { step: 500000, tickCount: 9, snapPoints: [1000000, 1000000, 1500000, 2000000, 2500000, 3000000, 3500000, 4000000, 4500000, 5000000] }
 */